import { z } from 'zod'
import { ActionSchema } from './action'
import { CellSchema } from './matrix'
import { PostureSchema } from './posture'
import { SurfaceRecipeSchema } from './recipes'
import { SeedNeedIdSchema } from './seed-needs'
import { ACTION_ID_RE, SCREEN_ID_RE } from './ids'

export const RoleVisibilitySchema = z.enum(['allow', 'deny'])

export const ScreenHeadingSchema = z.object({
  text: z.string().min(1),
  level: z.number().int().min(1).max(6).optional(),
})

export const ScreenCollectionSchema = z.object({
  enabled: z.literal(true),
  id: z.string().min(1),
})

export const ScreenDataSchema = z.object({
  queryKeys: z.array(z.string().min(1)).optional(),
  seedNeeds: z.array(SeedNeedIdSchema).optional(),
  empty: z
    .object({
      title: z.string().min(1),
      ctaActionId: z.string().min(1).optional(),
    })
    .optional(),
})

export const ScreenChromeSchema = z.object({
  navKey: z.string().min(1).optional(),
  shell: z.string().min(1).optional(),
})

export const ScreenAuthoritySchema = z.object({
  source_ref: z.string().min(1).optional(),
  intent_ids: z.array(z.string().min(1)).optional(),
})

/**
 * Canonical Screen IR row.
 * - recipe is a closed enum (no free string escape)
 * - cells is required (TJ-B); use expandDefaultCells when authoring
 * - seedNeeds tokens must be catalog ids
 */
export const ScreenSchema = z
  .object({
    app: z.string().min(1),
    screen_id: z.string().regex(SCREEN_ID_RE, 'screen_id must be dotted/kebab segment'),
    path: z.string().min(1),
    params: z.array(z.string().min(1)).optional(),
    heading: ScreenHeadingSchema,
    roles: z.record(RoleVisibilitySchema).refine((r) => Object.keys(r).length > 0, {
      message: 'roles must list at least one role',
    }),
    capability_refs: z.array(z.string().min(1)).optional(),
    states: z.array(PostureSchema).default(['ready']),
    actions: z.array(ActionSchema).default([]),
    /** Explicit consent, distinct from affordance presence in cells[].actions_present. */
    executable_action_ids_by_role: z.record(z.array(z.string().regex(ACTION_ID_RE))).optional(),
    recipe: SurfaceRecipeSchema,
    cells: z.array(CellSchema).min(1, 'cells required — expand from roles×states or declare explicitly'),
    collection: ScreenCollectionSchema.optional(),
    data: ScreenDataSchema.optional(),
    chrome: ScreenChromeSchema.optional(),
    authority: ScreenAuthoritySchema.optional(),
  })
  .superRefine((screen, ctx) => {
    // Capability policy: when STRICT_CAPABILITY=1 and roles include allow, capability_refs required.
    // Guard the Node global: this refinement also executes in browser bundles (defineScreen
    // at runtime), where `process` is undefined and an unguarded read white-screens the app.
    if (typeof process !== 'undefined' && process.env?.STRICT_CAPABILITY === '1') {
      const hasAllow = Object.values(screen.roles).some((v) => v === 'allow')
      if (hasAllow && (!screen.capability_refs || screen.capability_refs.length === 0)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'capability_refs required when STRICT_CAPABILITY=1 and any role is allow',
          path: ['capability_refs'],
        })
      }
    }

    const actionById = new Map<string, (typeof screen.actions)[number]>()
    for (const [i, action] of screen.actions.entries()) {
      if (actionById.has(action.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `duplicate action id "${action.id}"`,
          path: ['actions', i, 'id'],
        })
      }
      actionById.set(action.id, action)
    }

    const cellsByRoleState = new Map<string, (typeof screen.cells)[number]>()
    for (const [i, cell] of screen.cells.entries()) {
      const key = `${cell.role}::${cell.state}`
      if (cellsByRoleState.has(key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `duplicate cell for role "${cell.role}" and state "${cell.state}"`,
          path: ['cells', i],
        })
      }
      cellsByRoleState.set(key, cell)
    }
    const seenStates = new Set<string>()
    for (const [i, state] of screen.states.entries()) {
      if (seenStates.has(state)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `duplicate screen state "${state}"`,
          path: ['states', i],
        })
      }
      seenStates.add(state)
    }


    for (const [role, actionIds] of Object.entries(screen.executable_action_ids_by_role ?? {})) {
      if (screen.roles[role] !== 'allow') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `executable actions require declared allow role "${role}"`,
          path: ['executable_action_ids_by_role', role],
        })
        continue
      }

      const ready = cellsByRoleState.get(`${role}::ready`)
      if (!ready || ready.visibility !== 'allow') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `executable actions require one allow ready cell for role "${role}"`,
          path: ['executable_action_ids_by_role', role],
        })
        continue
      }

      const seenActionIds = new Set<string>()
      for (const [i, actionId] of actionIds.entries()) {
        if (seenActionIds.has(actionId)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `duplicate executable action id "${actionId}" for role "${role}"`,
            path: ['executable_action_ids_by_role', role, i],
          })
        }
        seenActionIds.add(actionId)

        const action = actionById.get(actionId)
        if (!action) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `executable action "${actionId}" is not declared`,
            path: ['executable_action_ids_by_role', role, i],
          })
          continue
        }
        if (!ready.actions_present.includes(actionId)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `executable action "${actionId}" is not present in role "${role}" ready cell`,
            path: ['executable_action_ids_by_role', role, i],
          })
        }
        if (action.roles && !action.roles.includes(role)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `executable action "${actionId}" excludes role "${role}"`,
            path: ['actions'],
          })
        }
        if (!action.driver || !action.safety || action.args === undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `executable action "${actionId}" requires driver, safety, and args`,
            path: ['actions'],
          })
        }
        if (action.safety && action.safety !== 'read-only') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `executable action "${actionId}" must be read-only until lifecycle execution is wired`,
            path: ['actions'],
          })
        }
      }
    }
    const roleKeys = new Set(Object.keys(screen.roles))
    for (const [i, cell] of screen.cells.entries()) {
      if (!roleKeys.has(cell.role)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `cell role "${cell.role}" not declared in roles`,
          path: ['cells', i, 'role'],
        })
      }
      if (screen.roles[cell.role] === 'deny' && cell.visibility === 'allow') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `cell for denied role "${cell.role}" cannot have visibility allow`,
          path: ['cells', i, 'visibility'],
        })
      }
      if (!seenStates.has(cell.state)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `cell state "${cell.state}" is not declared in screen.states`,
          path: ['cells', i, 'state'],
        })
      }
    }
  })

export type Screen = z.infer<typeof ScreenSchema>
